// React
import React from "react";
import { Controller } from "react-hook-form";

// MUI
import { Autocomplete, ListItem, TextField } from "@mui/material";
import { styled, lighten, darken } from "@mui/system";

// Components

// Hooks
import { useEnums, useFlags } from "@/store/hooks";

// Types
// Types
import type { FlagRead } from "@/types/api/types";
import FlagsChip from "@/components/flags/FlagsChip.tsx";

type FlagSelectProps = {
  name: string;
  control: any;
  categories: number[];
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: "small" | "medium";
  placeholder?: string;
}

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  color: theme.palette.primary.main,
  backgroundColor: lighten(theme.palette.primary.light, 0.96),
  ...theme.applyStyles("dark", {
    backgroundColor: darken(theme.palette.primary.main, 0.8),
  }),
}));

const GroupItems = styled("ul")({
  padding: 0,
});

const FlagsSelect = ({
  name,
  control,
  categories,
  label = "Tags",
  multiple = true,
  disabled = false,
  error = false,
  helperText,
  size = "small",
  placeholder,
}: FlagSelectProps) => {
  const flags = useFlags();

  const availableFlags = flags.filter(
    (flag: FlagRead) =>
      flag.available && categories.includes(flag.category.value)
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const { value, onChange } = field;

        return (
          <Autocomplete
            size={size}
            multiple={multiple}
            groupBy={(option: FlagRead) => option.category.label}
            renderGroup={(params) => {
              return (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                size={size}
                label={label}
                placeholder={placeholder}
                error={error}
                helperText={helperText}
              />
            )}
            options={availableFlags}
            isOptionEqualToValue={(option: FlagRead, value: FlagRead) =>
              option.uuid === value.uuid
            }
            value={value ?? []}
            onChange={(event: any, newValue: FlagRead[] | FlagRead | null) => {
              onChange(newValue);
            }}
            noOptionsText={"Aucun tag"}
            getOptionLabel={(option: FlagRead) => option.name}
            renderOption={(props, option: FlagRead) => {
              const { key, ...optionProps } = props;
              return (
                <ListItem
                  key={key}
                  sx={{
                    borderRadius: "8px",
                    margin: "5px",
                    padding: "8px",
                  }}
                  {...optionProps}
                >
                  <FlagsChip flag={option} showCategory={false} />
                </ListItem>
              );
            }}
          />
        );
      }}
    />
  );
};

export default FlagsSelect;
